/**
 * Research Registry pre-registration form choices.
 * Keep in sync with backend/research_registry/choices.py — values are the API's
 * wire format, labels are the client doc's wording verbatim.
 */

export const ELECTION_TYPE = [
  { value: 'presidential_general', label: 'Presidential General Election' },
  { value: 'midterm_general', label: 'Midterm General Election' },
  { value: 'other_general', label: 'Other General Election' },
  { value: 'special', label: 'Special Election' },
  { value: 'primary', label: 'Primary Election' },
  { value: 'multiple', label: 'Multiple elections' },
];

export const OFFICE_TOP_BALLOT = [
  { value: 'presidential', label: 'Presidential Race' },
  { value: 'us_senate', label: 'U.S. Senate Race' },
  { value: 'us_house', label: 'U.S. House Race' },
  { value: 'gubernatorial', label: 'Gubernatorial Race' },
  { value: 'state_legislative', label: 'State Legislative Race' },
  { value: 'other_statewide', label: 'Other Statewide Race' },
  { value: 'local_municipal', label: 'Local Municipal Race' },
  { value: 'ballot_measure', label: 'Ballot Measure' },
  { value: 'other', label: 'Other Office or Contest' },
];

export const PROGRAM_GOAL = [{ value: 'gotv', label: 'Voter Turnout / GOTV' }];

export const OUTCOME = [{ value: 'turnout', label: 'Turnout' }];

export const ELECTION_GEO_TARGET = [
  { value: 'precinct', label: 'Precinct' },
  { value: 'county', label: 'County' },
  { value: 'state_legislative_district', label: 'State legislative district' },
  { value: 'federal_congressional_district', label: 'Federal congressional district' },
  { value: 'statewide', label: 'Statewide' },
  { value: 'nationwide', label: 'Nationwide' },
  { value: 'designated_market_area', label: 'Designated Market Area' },
  { value: 'zip_code', label: 'ZIP Code' },
  { value: 'other', label: 'Other - Write in' },
];

export const FUNDING_STATUS = [
  { value: '501c3', label: '501(c)(3)' },
  { value: '501c4_primary_purpose', label: '501(c)(4) — Primary Purpose' },
  { value: '501c4_unrestricted', label: '501(c)(4) — Unrestricted' },
  { value: '501c5', label: '501(c)(5)' },
  { value: '501c6', label: '501(c)(6)' },
  { value: '527', label: '527 / Political Organization' },
  { value: 'other', label: 'Other' },
];

export const POLITICAL_ORG_TYPE_527 = [
  { value: 'candidate_committee', label: 'Candidate/authorized campaign committee' },
  { value: 'party_committee', label: 'Political party committee' },
  { value: 'super_pac', label: 'Independent expenditure-only committee / Super PAC' },
  { value: 'other_pac', label: 'Other PAC or political committee' },
  { value: 'state_local', label: 'State/local political organization' },
  { value: 'other_527', label: 'Other 527 organization' },
];

export const REFERENCE_GROUP = [
  {
    value: 'ab_test',
    label: 'A/B test (no reference group or a standard practice intervention as the reference group)',
  },
  { value: 'control_no_contact', label: 'Control - No contact' },
  { value: 'placebo', label: 'Placebo - Contact unrelated to the intervention' },
];
export const REFERENCE_GROUP_CONTROL_NO_CONTACT = 'control_no_contact';

export const OUTCOME_DATA_SOURCE = [
  { value: 'catalist', label: 'Catalist' },
  { value: 'targetsmart', label: 'TargetSmart' },
  { value: 'other', label: 'Other - Write in' },
];

// The voter-ID column a data file must start with, by outcome source. Mirrors the backend's
// VOTER_FILE_ID_TYPES; a write-in ("Other") source uses its own ID name.
export const VOTER_ID_COLUMN = { catalist: 'dwid', targetsmart: 'voterbase_id' };

export const CONTACT_MODE = [
  { value: 'cold_sms', label: 'Cold SMS/MMS' },
  { value: 'digital', label: 'Digital' },
  { value: 'door_to_door', label: 'Door-to-door canvass' },
  { value: 'email', label: 'Email' },
  { value: 'other_mail', label: 'Other Mail (Not Social Pressure)' },
  { value: 'other_mode', label: 'Other Mode of Contact' },
  { value: 'phone', label: 'Phone' },
  { value: 'radio', label: 'Radio' },
  { value: 'relational', label: 'Relational' },
  { value: 'robocall', label: 'Robocall' },
  { value: 'site_based_canvass', label: 'Site-Based Canvass' },
  { value: 'social_pressure_mail', label: 'Social Pressure Mail' },
  { value: 'broadcast_cable_tv', label: 'Broadcast/Cable TV' },
  { value: 'connected_tv', label: 'Connected TV' },
  { value: 'warm_sms', label: 'Warm SMS/MMS' },
];

export const REGRESSION_MODEL = [
  { value: 'linear', label: 'Linear regression' },
  { value: 'logit', label: 'Logistic regression (Logit)' },
  { value: 'negative_binomial', label: 'Negative binomial' },
  { value: 'other', label: 'Other, please enter' },
];

export const EMBARGO_PERIOD = [
  { value: 'after_election', label: "After this year's election" },
  { value: 'six_months', label: 'Six months after the election' },
  { value: 'one_year', label: 'One year after the election' },
  { value: 'two_years', label: 'Two years after the election' },
];

export const POST_EMBARGO_ACCESS = [
  { value: 'community', label: 'Access to the full community' },
  { value: 'by_request', label: 'By request' },
  { value: 'none', label: 'No access to the community (not recommended)' },
];

export const DOCUMENT_CATEGORY = [
  { value: 'experimental_protocol', label: 'Experimental protocol' },
  { value: 'randomization_plan', label: 'Randomization plan' },
  { value: 'pre_analysis_plan', label: 'Pre-analysis plan' },
  { value: 'implementation_plan', label: 'Implementation plan' },
  { value: 'data_dictionary', label: 'Data dictionary' },
  { value: 'other', label: 'Other' },
];

export const FILE_KIND = {
  DATA: 'data',
  ADDITIONAL_DOC: 'additional_doc',
  CREATIVE: 'creative',
};

export const EXPERIMENT_STATUS = {
  DRAFT: 'draft',
  VALIDATING: 'validating',
  VERIFY_COUNTS: 'verify_counts',
  NEEDS_APPROVAL: 'needs_approval',
  NEEDS_UPDATES: 'needs_updates',
  REGISTERED: 'registered',
};

export const PROGRAM_YEARS = Array.from({ length: 5 }, (_, i) => 2026 + i).map((year) => ({
  value: year,
  label: String(year),
}));
